import { useUser, SignInButton, UserButton } from '@clerk/clerk-react';
import { useUserRegistrations } from '@/hooks/useAuth';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Separator } from '@/components/ui/Separator';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  User,
  Calendar,
  Trophy,
  CreditCard,
  ChevronRight,
} from 'lucide-react';

export const Route = createFileRoute('/account')({
  component: AccountPage,
});

function AccountPage() {
  const { isSignedIn, isLoaded, user } = useUser();
  const {
    eventRegistration,
    tournamentRegistrations,
    activityRegistrations,
    orders,
    isLoading,
    hasEventRegistration,
  } = useUserRegistrations();

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="container py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to view your account and registrations
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <SignInButton mode="modal">
              <Button size="lg">Sign In</Button>
            </SignInButton>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <UserButton
            appearance={{
              elements: {
                avatarBox: 'w-16 h-16',
              },
            }}
          />
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {user?.firstName} {user?.lastName}
            </h1>
            <p className="text-muted-foreground">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
        </div>
        {!hasEventRegistration && (
          <Link to="/register">
            <Button>Register for Event</Button>
          </Link>
        )}
      </div>

      <Separator />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Event Registration */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Event Registration</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {eventRegistration ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant="success">Registered</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Entry Type</span>
                    <span className="font-medium capitalize">{eventRegistration.entry_type}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Registered On</span>
                    <span className="font-medium">
                      {formatDate(eventRegistration.created_at)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">
                    You haven't registered for this event yet
                  </p>
                  <Link to="/register">
                    <Button variant="outline">Register Now</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tournament Registrations */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Tournaments</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {tournamentRegistrations.length > 0 ? (
                <div className="space-y-3">
                  {tournamentRegistrations.map((reg) => (
                    <div
                      key={reg.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{reg.tournaments?.name || 'Tournament'}</p>
                        <p className="text-sm text-muted-foreground">
                          {reg.tournaments?.weapon && (
                            <span className="capitalize">{reg.tournaments.weapon}</span>
                          )}
                        </p>
                      </div>
                      <Badge variant="secondary">{formatCurrency(reg.amount_paid)}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No tournament registrations
                </p>
              )}
            </CardContent>
          </Card>

          {/* Activity Registrations */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Activities</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {activityRegistrations.length > 0 ? (
                <div className="space-y-3">
                  {activityRegistrations.map((reg) => (
                    <div
                      key={reg.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{reg.activities?.title || 'Activity'}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {reg.activities?.type}
                        </p>
                      </div>
                      <Badge variant="secondary">{formatCurrency(reg.amount_paid)}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No activity registrations
                </p>
              )}
            </CardContent>
          </Card>

          {/* Orders */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Order History</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          Order #{order.id.slice(0, 8)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            order.payment_status === 'completed'
                              ? 'success'
                              : order.payment_status === 'refunded'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {order.payment_status}
                        </Badge>
                        <span className="font-medium">{formatCurrency(order.total)}</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No orders yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
